import { ApolloError } from "@apollo/client";

import { isRecord } from "../../utils/record";

/*
 * The server puts the real HTTP status of a failure in `extensions.status`
 * of each GraphQL error, while the HTTP response itself is a 500. These
 * helpers read it back from every shape the error can reach us in.
 */

type ErrorStatus = number | undefined;

const readStatus = (graphQlError: unknown): ErrorStatus => {
  if (!isRecord(graphQlError) || !isRecord(graphQlError.extensions)) {
    return undefined;
  }
  const { status } = graphQlError.extensions;
  return typeof status === "number" ? status : undefined;
};

const readResultErrors = (error: unknown): unknown[] | undefined => {
  if (!isRecord(error) || !isRecord(error.result)) {
    return undefined;
  }
  const { errors } = error.result;
  return Array.isArray(errors) ? errors : undefined;
};

/** One entry per GraphQL error found on `error`; `undefined` when it has no status. */
const collectStatuses = (error: unknown): ErrorStatus[] => {
  if (error instanceof ApolloError) {
    return [
      ...error.graphQLErrors.map(readStatus),
      ...(readResultErrors(error.networkError) ?? []).map(readStatus),
    ];
  }

  const resultErrors = readResultErrors(error);
  if (resultErrors) {
    return resultErrors.map(readStatus);
  }

  if (isRecord(error) && isRecord(error.extensions)) {
    return [readStatus(error)];
  }

  return [];
};

/** Highest `extensions.status` across all the GraphQL errors, if any has one. */
export const getGraphQlErrorStatus = (error: unknown): number | undefined => {
  const statuses = collectStatuses(error).filter(
    (status): status is number => status !== undefined
  );
  return statuses.length > 0 ? Math.max(...statuses) : undefined;
};

/**
 * True only when every GraphQL error carries a 4xx status. An error without
 * a status is an unexpected crash and must still be reported.
 */
export const isExpectedGraphQlError = (error: unknown): boolean => {
  const statuses = collectStatuses(error);
  return (
    statuses.length > 0 &&
    statuses.every(
      (status) => status !== undefined && status >= 400 && status < 500
    )
  );
};

/*
 * `ApolloError` does not carry the operation name, but it keeps the error
 * objects the error link saw: the network error of a failed request, and
 * the GraphQL errors of a response that arrived as HTTP 200 with `errors`.
 * The name is keyed on those objects.
 */
const operationNames = new WeakMap<object, string>();

export const rememberGraphQlOperation = (
  error: object,
  operationName: string
): void => {
  operationNames.set(error, operationName);
};

const lookUpOperation = (error: unknown): string | undefined =>
  isRecord(error) ? operationNames.get(error) : undefined;

export const getGraphQlOperationName = (error: unknown): string | undefined => {
  if (!(error instanceof ApolloError)) {
    return lookUpOperation(error);
  }
  return (
    lookUpOperation(error.networkError) ??
    error.graphQLErrors.map(lookUpOperation).find(Boolean)
  );
};

/*
 * The Sentry link captures every unexpected GraphQL error of an HTTP 200
 * response itself, so the call site reporting the same failure through the
 * rejected `ApolloError` would be a duplicate. These are the errors it took.
 */
const capturedGraphQlErrors = new WeakSet<object>();

export const markGraphQlErrorCaptured = (graphQlError: object): void => {
  capturedGraphQlErrors.add(graphQlError);
};

export const isGraphQlErrorCaptured = (graphQlError: unknown): boolean =>
  isRecord(graphQlError) && capturedGraphQlErrors.has(graphQlError);

/**
 * True when `error` is a failure the Sentry link already reported: every
 * GraphQL error in it was captured there or is an expected 4xx that nobody
 * reports. A non-2xx response that still carried a GraphQL body is covered
 * through the errors of its `networkError.result`, which are the very objects
 * the link saw. A network failure without such errors is never covered.
 */
export const isGraphQlErrorAlreadyCaptured = (error: unknown): boolean => {
  if (!(error instanceof ApolloError)) {
    return false;
  }
  const graphQlErrors = error.networkError
    ? readResultErrors(error.networkError) ?? []
    : error.graphQLErrors;
  return (
    graphQlErrors.some(isGraphQlErrorCaptured) &&
    graphQlErrors.every(
      (graphQlError) =>
        isGraphQlErrorCaptured(graphQlError) ||
        isExpectedGraphQlError(graphQlError)
    )
  );
};
